import { SUBTRAMITES_CONFIG } from "@/subtramites/constants";
import { getSubtramiteFromTramite } from "@/subtramites/getSubtramiteFromTramite";
import { SubtramiteEstado, SubtramiteKey } from "@/subtramites/types";
import { FileText, Map, Building, FileSearch } from 'lucide-react';

export interface GroupedSubtramite {
  label: string;
  status: SubtramiteEstado;
  key: SubtramiteKey;
}

export interface SubtramiteGroup {
  categoryId: string;
  categoryLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  subtramites: GroupedSubtramite[];
  stats: {
    'en-proceso': number;
    'finalizado': number;
  };
}

// Metadata de categorías con iconos y labels
const categoryMetadata: Record<string, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  'certificados': { label: 'Certificados', icon: FileText },
  'planos': { label: 'Planos', icon: Map },
  'tramites': { label: 'Trámites', icon: Building },
  'estudio-titulo': { label: 'Estudio de Título', icon: FileSearch },
};

// Orden de categorías según tabs del dashboard
const CATEGORY_ORDER = ['certificados', 'planos', 'tramites', 'estudio-titulo'];

export const useGroupedSubtramites = (
  tramite: any,
  showOnlyStarted: boolean = true
): SubtramiteGroup[] => {
  // 1. Obtener todos los subtramites con su información
  const subtramitesWithCategory = Object.entries(SUBTRAMITES_CONFIG).map(([key, config]) => {
    const subtramite = getSubtramiteFromTramite(key as SubtramiteKey, tramite);

    return {
      key: key as SubtramiteKey,
      label: subtramite?.label ?? (typeof config.label === 'function' ? config.label(subtramite) : config.label),
      status: (subtramite?.estado ?? 'pendiente') as SubtramiteEstado,
      category: config.category,
    };
  });

  // 2. Filtrar por showOnlyStarted si es necesario
  const filteredSubtramites = showOnlyStarted
    ? subtramitesWithCategory.filter(s => s.status !== 'pendiente')
    : subtramitesWithCategory;

  // 3. Agrupar por categoría
  const groupedByCategory = filteredSubtramites.reduce((acc, subtramite) => {
    const category = subtramite.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push({
      key: subtramite.key,
      label: subtramite.label,
      status: subtramite.status,
    });
    return acc;
  }, {} as Record<string, GroupedSubtramite[]>);

  // 4. Crear grupos con metadata y estadísticas
  const groups: SubtramiteGroup[] = Object.entries(groupedByCategory).map(([categoryId, subtramites]) => {
    const metadata = categoryMetadata[categoryId] || { label: categoryId, icon: FileText };

    // Calcular estadísticas del grupo
    const stats = {
      'en-proceso': subtramites.filter(s => s.status === 'en-proceso').length,
      'finalizado': subtramites.filter(s => s.status === 'finalizado').length,
    };

    return {
      categoryId,
      categoryLabel: metadata.label,
      icon: metadata.icon,
      subtramites,
      stats,
    };
  });

  // 5. Ordenar grupos según el orden de tabs del dashboard
  groups.sort((a, b) => {
    const indexA = CATEGORY_ORDER.indexOf(a.categoryId);
    const indexB = CATEGORY_ORDER.indexOf(b.categoryId);

    // Si alguna categoría no está en el orden definido, ponerla al final
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });

  // 6. Filtrar grupos vacíos (sin subtramites iniciados)
  return groups.filter(group => group.subtramites.length > 0);
};
